import React from "react";
import { Link } from "react-router-dom";
import Countdown from "../components/Countdown";
import { ASSETS, GALLERY } from "../lib/api";

const Btn = ({ to, children, testId, variant = "gold" }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`btn-gold inline-flex items-center justify-center border border-gold text-[11px] tracking-[0.25em] uppercase px-7 py-3.5 rounded-full transition-colors ${
      variant === "gold" ? "text-gold" : "bg-ink text-ivory border-ink"
    }`}
  >
    {children}
  </Link>
);

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-24 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 relative z-10">
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-6">A New Chapter · May 8, 2027</div>
            <h1 className="font-serif text-[54px] sm:text-7xl lg:text-[92px] leading-[0.95] tracking-tighter text-ink">
              Anjola
              <span className="block italic text-gold font-light">&amp;</span>
              Michael
            </h1>
            <p className="mt-8 text-base sm:text-lg text-ink-soft max-w-md leading-relaxed">
              Two Hearts. Two Families.<br /> One Beautiful Celebration.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Btn to="/rsvp" testId="hero-rsvp-btn">RSVP · Traditional</Btn>
              <Btn to="/traditional" testId="hero-details-btn">View Details</Btn>
              <Btn to="/registry" testId="hero-registry-btn">Registry</Btn>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] max-w-md ml-auto floaty">
              <div className="absolute -inset-3 border border-gold/50" aria-hidden />
              <img
                src={ASSETS.hero}
                alt="Anjola and Michael"
                loading="eager"
                className="relative w-full h-full object-cover"
                style={{ objectPosition: "50% 35%" }}
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HAIRLINE + COUNTDOWN */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="hairline mb-10"><span>A · M</span></div>
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">Countdown · Traditional Ceremony</div>
            <div className="font-serif text-3xl sm:text-4xl text-ink leading-tight tracking-tight">
              Until we say <em className="text-gold not-italic">yes,</em> together.
            </div>
            <div className="mt-3 text-xs text-ink-faint tracking-[0.15em] uppercase">May 8, 2027 · Balmoral, Lagos</div>
          </div>
          <div className="lg:col-span-7">
            <Countdown />
          </div>
        </div>
      </section>

      {/* FAMILY WELCOME */}
      <section className="bg-cream/60 border-y border-hairline">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">A Note From Our Families</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-none tracking-tight">
              A beautiful new chapter is about to begin.
            </h2>
            <div className="mt-8">
              <img src={ASSETS.coupleEmbrace} alt="Anjola and Michael" loading="lazy" className="w-full aspect-[3/4] object-cover" data-testid="family-image" />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pt-14 text-ink-soft leading-[1.9] text-[15px] sm:text-base">
            <p>
              With grateful hearts, the families of <span className="text-ink">Bola &amp; Shola Temowo</span> and
              {" "}<span className="text-ink">Mike &amp; Susan Itemuagbor</span> are delighted to announce the
              upcoming Traditional Wedding Ceremony of their beloved children,
              {" "}<span className="text-ink italic font-serif">Anjola and Michael</span>.
            </p>
            <p className="mt-5">
              We would be honored to have you celebrate this special moment with us as two hearts, two families, and one beautiful love story come together.
            </p>
            <p className="mt-5">
              Please save the date and kindly confirm your attendance through the RSVP page.
            </p>
            <p className="mt-5">
              We cannot wait to celebrate, share beautiful moments, and create lasting memories with you.
            </p>
            <div className="mt-10 font-serif text-2xl text-ink italic">With love,</div>
            <div className="text-gold text-sm tracking-[0.2em] uppercase mt-1">The Temowo &amp; Itemuagbor Families</div>
          </div>
        </div>
      </section>

      {/* OUR FAMILIES */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24" data-testid="families-section">
        <div className="hairline mb-16"><span>Our Families</span></div>
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
          <div className="border border-hairline bg-ivory p-10 text-center" data-testid="family-temowo">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">The Bride&rsquo;s Family</div>
            <div className="font-serif text-3xl sm:text-4xl text-ink leading-tight tracking-tight">
              Bola &amp; Shola<br />
              <span className="text-gold italic font-light">Temowo</span>
            </div>
            <p className="mt-6 text-sm text-ink-soft leading-relaxed max-w-sm mx-auto">
              Anjola&rsquo;s beloved parents, gratefully welcoming Michael into the family and inviting you to celebrate this new chapter with them.
            </p>
          </div>

          <div className="border border-hairline bg-ivory p-10 text-center" data-testid="family-itemuagbor">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">The Groom&rsquo;s Family</div>
            <div className="font-serif text-3xl sm:text-4xl text-ink leading-tight tracking-tight">
              Mike &amp; Susan<br />
              <span className="text-gold italic font-light">Itemuagbor</span>
            </div>
            <p className="mt-6 text-sm text-ink-soft leading-relaxed max-w-sm mx-auto">
              Michael&rsquo;s beloved parents, joyfully welcoming Anjola into the family and inviting you to share in this beautiful union.
            </p>
          </div>
        </div>
        <div className="mt-14 text-center font-serif text-2xl italic text-ink">— With love, the two families</div>
      </section>

      {/* LOVE STORY GALLERY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="gallery-section">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-3">Our Love Story</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-none tracking-tight">
              A few of our <em className="text-gold not-italic italic">favourite</em> moments.
            </h2>
          </div>
          <div className="lg:col-span-6 text-ink-soft leading-relaxed">
            Every glance, every laugh, every stolen moment — it&rsquo;s been leading us here.
            We can&rsquo;t wait to add you to the next chapter.
          </div>
        </div>
        <div className="grid grid-cols-6 gap-3 md:gap-5 auto-rows-[180px] md:auto-rows-[220px]">
          {GALLERY.map((g, i) => {
            // Featured landscape: full width, 2 rows tall (widescreen banner)
            // Featured portrait: 3 cols x 2 rows tall
            // Landscape: 3 cols x 1 row
            // Portrait: 2 cols x 1 row (or 3 on mobile)
            let cls = "col-span-3 sm:col-span-2 row-span-1";
            if (g.featured && g.orientation === "landscape") cls = "col-span-6 row-span-2";
            else if (g.featured) cls = "col-span-6 sm:col-span-3 row-span-2";
            else if (g.orientation === "landscape") cls = "col-span-6 sm:col-span-3 row-span-1";
            return (
              <figure key={g.src} data-testid={`gallery-item-${i}`} className={`group relative overflow-hidden bg-cream ${cls}`}>
                <img src={g.src} alt={g.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
                <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-ink/60 to-transparent text-[10px] tracking-[0.3em] uppercase text-ivory opacity-0 group-hover:opacity-100 transition-opacity">
                  {g.label}
                </figcaption>
              </figure>
            );
          })}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {[
            { to: "/traditional", label: "Traditional Wedding", copy: "May 8, 2027 · Balmoral. The ceremony that begins it all.", testId: "card-traditional" },
            { to: "/registry", label: "Registry", copy: "Your love and presence are our greatest gifts. Registry details soon.", testId: "card-registry" },
          ].map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.testId} className="group block border border-hairline bg-ivory p-8 hover:border-gold transition-colors">
              <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">Explore</div>
              <div className="font-serif text-2xl text-ink leading-tight">{c.label}</div>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">{c.copy}</p>
              <div className="mt-6 text-[11px] tracking-[0.25em] uppercase text-ink group-hover:text-gold transition-colors">Enter →</div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
